import React, { useState } from 'react';
import { useSpring } from '@react-spring/web';
import NextImage, { StaticImageData } from 'next/image';
import { useTheme } from 'styled-components';
import { Column, Tick } from '@solent-university/solent-icons';

import type { LinkProps } from 'Elements/Link';

import Pill from 'Elements/Pill/Pill';

import {
    Container,
    ImageContainer,
    ImageLink,
    ImageWrapper,
    Inner,
    NameLink,
    DegreeType,
    Name,
    CompareButton,
    Icon,
    CategoriesList,
    PillItem,
    VariantsHeading,
    VariantList,
    VariantItem,
    VariantLink
} from './Course.styled';

export interface Props {
    degreeType: string;
    courseName: string;
    link: string;
    studyLevel?: string;
    categories?: string[];
    variants?: LinkProps[];
    image: {
        src: StaticImageData | string;
    };
    status: 'open' | 'closed' | 'clearing';
    disabledCompareChecked?: boolean;
    defaultCompareChecked?: boolean;
    addCourseToCompare?: () => void;
    removeCourseToCompare?: () => void;
}

const Course: React.FC<Props> = props => {
    const {
        degreeType,
        courseName,
        link,
        studyLevel,
        categories,
        variants,
        image,
        status,
        defaultCompareChecked = false,
        disabledCompareChecked = false,
        addCourseToCompare,
        removeCourseToCompare
    } = props;

    const { colors } = useTheme();
    const [isHovering, setIsHovering] = useState(false);

    const imageSpring = useSpring({
        transform: isHovering ? 'scale(1.03)' : 'scale(1)'
    });

    const handleCompareCheck = () => {
        if (addCourseToCompare && removeCourseToCompare) {
            if (!defaultCompareChecked) {
                addCourseToCompare();
            } else {
                removeCourseToCompare();
            }
        }
    };

    return (
        <Container className="c-card c-course-card">
            <ImageContainer>
                <ImageLink
                    href={link}
                    aria-label={courseName}
                    data-hover={isHovering}
                    onMouseOver={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                >
                    <ImageWrapper style={imageSpring}>
                        <NextImage {...image} alt="" fill style={{ objectFit: 'cover' }} />
                    </ImageWrapper>
                </ImageLink>
            </ImageContainer>
            <Inner>
                <NameLink href={link} aria-label={courseName}>
                    <DegreeType>{degreeType}</DegreeType>
                    <Name>{courseName}</Name>
                </NameLink>
                <CompareButton
                    onClick={handleCompareCheck}
                    $checked={defaultCompareChecked}
                    aria-label={`Add/remove ${courseName} to course compare`}
                    disabled={disabledCompareChecked}
                >
                    Compare course
                    <Icon
                        as={defaultCompareChecked ? Tick : Column}
                        $checked={defaultCompareChecked}
                    />
                </CompareButton>
                <CategoriesList>
                    {studyLevel && (
                        <PillItem>
                            <Pill
                                textColor={
                                    studyLevel === 'Postgraduate'
                                        ? colors.accents[9].hex
                                        : colors.tertiary.hex
                                }
                                backgroundColor={
                                    studyLevel === 'Postgraduate'
                                        ? `rgb(${colors.accents[9].rgb}, 0.1)`
                                        : `rgb(${colors.tertiary.rgb}, 0.1)`
                                }
                            >
                                {studyLevel}
                            </Pill>
                        </PillItem>
                    )}
                    {categories &&
                        categories.length > 0 &&
                        categories.map((category, index) => (
                            <PillItem key={index}>
                                <Pill
                                    textColor={colors.accents[5].hex}
                                    backgroundColor={`rgb(${colors.accents[5].rgb}, 0.1)`}
                                >
                                    {category}
                                </Pill>
                            </PillItem>
                        ))}
                    {(status === 'clearing' || status === 'closed') && (
                        <PillItem>
                            <Pill
                                textColor={colors.primary.hex}
                                backgroundColor={`rgb(${colors.primary.rgb}, 0.1)`}
                            >
                                {status === 'clearing' ? 'Clearing open' : 'Closed'}
                            </Pill>
                        </PillItem>
                    )}
                </CategoriesList>
                {variants && variants.length > 0 && (
                    <>
                        <VariantsHeading>Also available as:</VariantsHeading>
                        <VariantList>
                            {variants.map(variant => (
                                <VariantItem key={variant.id}>
                                    <VariantLink {...variant} />
                                </VariantItem>
                            ))}
                        </VariantList>
                    </>
                )}
            </Inner>
        </Container>
    );
};

export default Course;
